import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import shared from "@/seo/shared";
import { useSeo } from "@/seo/useSeo";
import { SERVICES } from "@/components/site/data";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { SubHero } from "@/components/site/SubHero";
import { Contact } from "@/components/site/Contact";
import { Reveal } from "@/components/site/Reveal";

export const ServiceGrid = ({ city }) => (
  <div className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="service-grid">
    {shared.services.map((s) => {
      const d = SERVICES.find((x) => x.id === s.sid);
      return (
        <Link key={s.slug} to={`/${s.slug}`} className="group flex flex-col bg-paper p-6 transition-colors duration-500 hover:bg-ink" data-testid={`service-link-${s.slug}`}>
          {d && <d.icon size={26} className="text-terra-ink group-hover:text-terra" />}
          <h3 className="mt-5 font-display text-2xl font-bold uppercase leading-none text-ink group-hover:text-white">{s.name}{city ? ` in ${city}` : ""}</h3>
          <p className="mt-2 flex-1 text-sm text-slate-700 group-hover:text-slate-200">{s.description.split(".")[0]}.</p>
          <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-terra-ink group-hover:text-terra">Mehr erfahren <ArrowRight size={14} /></span>
        </Link>
      );
    })}
  </div>
);

export const LocationLinks = ({ exclude }) => (
  <ul className="flex flex-wrap gap-2" data-testid="location-links">
    {shared.locations.filter((l) => l.slug !== exclude).map((l) => (
      <li key={l.slug}><Link to={`/${l.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-4 py-2 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-ink hover:text-white"><MapPin size={14} />Dachdecker {l.city}</Link></li>
    ))}
  </ul>
);

export default function LocationPage({ l, consent, onCookies }) {
  useSeo({ path: `/${l.slug}`, title: l.title, description: l.description, ld: [shared.businessLd(), ...shared.locationLd(l)] });
  return (
    <>
      <Nav />
      <main data-testid={`location-page-${l.slug}`}>
        <SubHero h1={l.h1} lead={l.lead} img={l.img} alt={`Dachdecker in ${l.city} – Christian Bedachungen`}
          crumbs={[{ name: "Startseite", path: "/" }, { name: "Einsatzgebiet", path: "/einsatzgebiet" }, { name: l.city, path: `/${l.slug}` }]} />
        <section className="bg-paper py-20 text-ink sm:py-28">
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
            <div className="grid gap-12 md:grid-cols-2">
              {l.blocks.map((b, i) => (
                <Reveal key={b.h2} delay={(i % 2) * 0.08}>
                  <h2 className="font-display text-3xl font-bold uppercase leading-[0.95]">{b.h2}</h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-700">{b.text}</p>
                </Reveal>
              ))}
            </div>
            <div className="mt-20 max-w-4xl">
              <h2 className="font-display text-3xl font-bold uppercase leading-[0.95] sm:text-4xl">Häufige Fragen zum Dachdecker in {l.city}</h2>
              <Accordion type="single" collapsible className="mt-6 border-t border-ink/15" data-testid="location-faq">
                {l.faqs.map((f, i) => (
                  <AccordionItem key={f.q} value={`f-${i}`} className="border-b border-ink/15" data-testid={`location-faq-item-${i}`}>
                    <AccordionTrigger className="py-6 text-left font-display text-xl font-bold uppercase leading-tight text-ink hover:no-underline [&>svg]:text-terra-ink">{f.q}</AccordionTrigger>
                    <AccordionContent className="pb-6 text-base leading-relaxed text-slate-700">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
            <h2 className="mt-20 font-display text-4xl font-bold uppercase leading-none sm:text-5xl">Unsere Leistungen in {l.city}</h2>
            <div className="mt-8"><ServiceGrid city={l.city} /></div>
            <h2 className="mt-20 font-display text-3xl font-bold uppercase">Weitere Orte in unserem Einsatzgebiet</h2>
            <div className="mt-6"><LocationLinks exclude={l.slug} /></div>
            <Link to="/" className="btn-dark mt-10 w-fit" data-testid="location-home-link">Zur Startseite <ArrowRight size={16} className="btn-arrow" /></Link>
          </div>
        </section>
        <Contact consent={consent} />
      </main>
      <Footer onCookies={onCookies} />
    </>
  );
}
